import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { KanbanSquare, List, RefreshCw } from 'lucide-react'
import {
  Button,
  DataTable,
  EmptyState,
  PageHeaderBand,
  Select,
  ViewToggle,
} from '@/components/ui'
import { ConfirmShipDateModal } from '../components/ConfirmShipDateModal'
import { LogEstimateModal } from '../components/LogEstimateModal'
import { PoShipmentDetailDrawer } from '../components/PoShipmentDetailDrawer'
import { ShipConfirmationKanban } from '../components/ShipConfirmationKanban'
import { SplitLineModal } from '../components/SplitLineModal'
import { LineExpandedPanel } from '../components/LineExpandedPanel'
import {
  useConfirmAllLines,
  useConfirmShipDate,
  useCreateSplits,
  useLogShipDateEstimate,
  useShipConfirmationList,
  useTransitionShipConfirmation,
} from '../api/queries'
import type { ConfirmationSource, ShipConfirmationStage, ShipConfirmationTracker } from '../api/types'
import { buildLineColumns } from '../utils/line-columns'
import { groupTrackersByPo, type PoGroup } from '../utils/po-grouping'
import { STAGE_LABELS } from '../utils/ship-confirmation-stage-meta'

type ViewMode = 'table' | 'board'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'board' as const, label: 'Board', icon: KanbanSquare },
]

const STAGE_FILTER_OPTIONS: { value: ShipConfirmationStage | ''; label: string }[] = [
  { value: '', label: 'Active' },
  { value: 'awaiting_confirmation', label: STAGE_LABELS.awaiting_confirmation },
  { value: 'confirmed', label: STAGE_LABELS.confirmed },
  { value: 'received', label: STAGE_LABELS.received },
  { value: 'cancelled', label: STAGE_LABELS.cancelled },
]

function initialPoFilter(): string {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('po') ?? ''
}

/** Preferred line to prefill the modal from when confirming a whole PO
 *  group in bulk — an open line if there is one, otherwise whatever's
 *  there (all-confirmed groups are only reachable via the drawer's
 *  "Confirm all open lines", not the board, but this stays safe either
 *  way). */
function representativeLine(group: PoGroup): ShipConfirmationTracker {
  return group.lines.find((l) => l.stage === 'awaiting_confirmation') ?? group.lines[0]
}

/**
 * PO Ship Date Confirmation — flags open inbound POs without a
 * vendor-confirmed ship date. See `module.config.ts` for the full
 * incident/history context.
 */
export function PoShipDateConfirmationPage() {
  const [view, setView] = useState<ViewMode>('board')
  const [stageFilter, setStageFilter] = useState<ShipConfirmationStage | ''>('')
  const [poFilter, setPoFilter] = useState(initialPoFilter)

  const includeTerminal = stageFilter === 'received' || stageFilter === 'cancelled'
  const { data, isLoading, isFetching, refetch } = useShipConfirmationList(
    stageFilter || undefined,
    includeTerminal || undefined,
  )
  const transition = useTransitionShipConfirmation()
  const confirmShipDate = useConfirmShipDate()
  const confirmAllLines = useConfirmAllLines()
  const logEstimate = useLogShipDateEstimate()
  const createSplits = useCreateSplits()

  const [confirmTarget, setConfirmTarget] = useState<ShipConfirmationTracker | null>(null)
  const [bulkConfirmTarget, setBulkConfirmTarget] = useState<PoGroup | null>(null)
  const [estimateTarget, setEstimateTarget] = useState<ShipConfirmationTracker | null>(null)
  const [splitTarget, setSplitTarget] = useState<ShipConfirmationTracker | null>(null)
  const [detailPoId, setDetailPoId] = useState<number | null>(null)
  // Table-view row expand: reveals the split breakdown inline (mirrors
  // Sales Inbox's `expandedIntakeIds` pattern). Board view never shows
  // this — the kanban card is PO-level, splits are line-level detail.
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set())
  const toggleExpand = (id: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Sibling-line count for the "apply to all lines" checkbox — deliberately
  // a second, filter-independent fetch of the active board list (React
  // Query dedupes this against the default board view's own query), so a
  // stage-filtered table view doesn't undercount siblings sitting in a
  // different stage.
  const { data: activeRowsForSiblingCount } = useShipConfirmationList()
  const siblingLineCount = useMemo(() => {
    if (bulkConfirmTarget) return bulkConfirmTarget.totalCount
    if (!confirmTarget) return 0
    return (activeRowsForSiblingCount ?? []).filter(
      (r) => r.odoo_po_id === confirmTarget.odoo_po_id,
    ).length
  }, [activeRowsForSiblingCount, confirmTarget, bulkConfirmTarget])

  const allRows = data ?? []
  const rows = useMemo(() => {
    const q = poFilter.trim().toLowerCase()
    if (!q) return allRows
    return allRows.filter(
      (r) =>
        r.odoo_po_name.toLowerCase().includes(q) ||
        (r.vendor_name ?? '').toLowerCase().includes(q),
    )
  }, [allRows, poFilter])

  const poGroups = useMemo(() => groupTrackersByPo(rows), [rows])

  function advance(tracker: ShipConfirmationTracker, target: ShipConfirmationStage) {
    if (target === 'confirmed') {
      setConfirmTarget(tracker)
      return
    }
    transition.mutate(
      { trackerId: tracker.id, targetStage: target },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'transition failed', { duration: 5000 })
        },
      },
    )
  }

  function undoConfirmation(tracker: ShipConfirmationTracker) {
    advance(tracker, 'awaiting_confirmation')
  }

  /**
   * Board-level bulk advance — a kanban card here is a whole PO
   * (`PoGroup`), not a single line, so dragging it always acts on every
   * open line at once. Confirming needs evidence (opens the modal, in
   * locked-bulk mode via `bulkConfirmTarget`); undoing doesn't, so it
   * fans the existing single-tracker transition out over every currently
   * confirmed line rather than adding a new bulk-transition endpoint.
   */
  function advanceGroup(group: PoGroup, target: ShipConfirmationStage) {
    if (target === 'confirmed') {
      if (group.totalCount === 1) {
        setConfirmTarget(group.lines[0])
      } else {
        setBulkConfirmTarget(group)
      }
      return
    }

    const toUndo = group.lines.filter((l) => l.stage !== 'awaiting_confirmation')
    if (toUndo.length === 0) return
    Promise.all(
      toUndo.map((l) => transition.mutateAsync({ trackerId: l.id, targetStage: 'awaiting_confirmation' })),
    )
      .then(() => {
        toast.success(
          `${group.odoo_po_name}: ${toUndo.length} line${toUndo.length === 1 ? '' : 's'} reverted to awaiting confirmation`,
        )
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : 'undo failed', { duration: 5000 })
      })
  }

  const columns = useMemo(
    () =>
      buildLineColumns({
        onConfirm: setConfirmTarget,
        onLogEstimate: setEstimateTarget,
        onUndo: undoConfirmation,
        onSplit: setSplitTarget,
        expandedRowIds,
        onToggleExpand: toggleExpand,
      }),
    [expandedRowIds],
  )

  function submitConfirmShipDate(body: {
    confirmed_date: string
    source: ConfirmationSource
    detail?: string
  }) {
    if (!confirmTarget) return
    const isRevision = confirmTarget.stage === 'confirmed'
    confirmShipDate.mutate(
      { trackerId: confirmTarget.id, body },
      {
        onSuccess: () => {
          toast.success(
            isRevision
              ? `${confirmTarget.odoo_po_name} ship date revised`
              : `${confirmTarget.odoo_po_name} marked confirmed`,
          )
          setConfirmTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'confirmation failed', { duration: 6000 })
        },
      },
    )
  }

  function submitConfirmAllLines(body: {
    confirmed_date: string
    source: ConfirmationSource
    detail?: string
  }) {
    const odooPoId = confirmTarget?.odoo_po_id ?? bulkConfirmTarget?.odoo_po_id
    const odooPoName = confirmTarget?.odoo_po_name ?? bulkConfirmTarget?.odoo_po_name
    if (odooPoId == null) return
    confirmAllLines.mutate(
      { odooPoId, body },
      {
        onSuccess: ({ updated, skipped_split_lines }) => {
          let message = `${odooPoName}: ${updated.length} open line(s) confirmed`
          if (skipped_split_lines.length > 0) {
            message += `, ${skipped_split_lines.length} split line(s) skipped — confirm splits individually.`
          }
          toast.success(message)
          setConfirmTarget(null)
          setBulkConfirmTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'bulk confirmation failed', { duration: 6000 })
        },
      },
    )
  }

  function submitCreateSplits(splits: { quantity: string; latest_estimated_date?: string }[]) {
    if (!splitTarget) return
    createSplits.mutate(
      { trackerId: splitTarget.id, body: { splits } },
      {
        onSuccess: () => {
          toast.success(`${splitTarget.odoo_po_name} split into ${splits.length} shipments`)
          setExpandedRowIds((prev) => new Set(prev).add(String(splitTarget.id)))
          setSplitTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'split failed', { duration: 6000 })
        },
      },
    )
  }

  function submitLogEstimate(body: { estimated_date?: string; note: string }) {
    if (!estimateTarget) return
    logEstimate.mutate(
      { trackerId: estimateTarget.id, body },
      {
        onSuccess: () => {
          toast.success('Estimate update logged')
          setEstimateTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'failed to log update', { duration: 5000 })
        },
      },
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="PO Ship Date Confirmation"
        description="Open inbound POs without a vendor-confirmed ship date — the active follow-up backlog."
      >
        <div className="ml-auto flex items-center gap-3">
          <input
            type="text"
            value={poFilter}
            onChange={(e) => setPoFilter(e.target.value)}
            placeholder="Filter by PO or vendor…"
            aria-label="Filter by PO or vendor"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as ShipConfirmationStage | '')}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'active'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </Button>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
        </div>
      </PageHeaderBand>

      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-secondary">
            Loading ship confirmations…
          </div>
        ) : rows.length === 0 ? (
          <EmptyShipConfirmationState filtered={!!stageFilter || !!poFilter} />
        ) : view === 'table' ? (
          <div className="h-full overflow-auto">
            <DataTable
              data={rows}
              columns={columns}
              moduleId="po-ship-confirmation"
              getRowId={(row) => String(row.id)}
              expandedRowIds={expandedRowIds}
              renderExpandedRow={(tracker) => <LineExpandedPanel tracker={tracker} />}
            />
          </div>
        ) : (
          <div className="h-full overflow-auto p-4">
            <ShipConfirmationKanban
              items={poGroups}
              onSelect={(group) => setDetailPoId(group.odoo_po_id)}
              onAdvance={advanceGroup}
            />
          </div>
        )}
      </div>

      <PoShipmentDetailDrawer
        odooPoId={detailPoId}
        onClose={() => setDetailPoId(null)}
        onConfirmLine={setConfirmTarget}
        onLogEstimate={setEstimateTarget}
        onUndoLine={undoConfirmation}
        onSplitLine={setSplitTarget}
        onConfirmAllOpen={(openLines) => {
          const [group] = groupTrackersByPo(openLines)
          if (group) setBulkConfirmTarget(group)
        }}
      />

      <ConfirmShipDateModal
        tracker={confirmTarget ?? (bulkConfirmTarget ? representativeLine(bulkConfirmTarget) : null)}
        isPending={confirmShipDate.isPending || confirmAllLines.isPending}
        siblingLineCount={siblingLineCount}
        lockApplyAll={!!bulkConfirmTarget}
        onCancel={() => {
          setConfirmTarget(null)
          setBulkConfirmTarget(null)
        }}
        onConfirm={submitConfirmShipDate}
        onConfirmAll={submitConfirmAllLines}
      />
      <LogEstimateModal
        tracker={estimateTarget}
        isPending={logEstimate.isPending}
        onCancel={() => setEstimateTarget(null)}
        onSubmit={submitLogEstimate}
      />
      <SplitLineModal
        tracker={splitTarget}
        isPending={createSplits.isPending}
        onCancel={() => setSplitTarget(null)}
        onSubmit={submitCreateSplits}
      />
    </div>
  )
}

function EmptyShipConfirmationState({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-8">
        <EmptyState
          title="No confirmations match this filter"
          description="Try a different stage or clear the PO/vendor filter."
        />
      </div>
    )
  }
  return (
    <div className="flex h-full items-center justify-center px-6 py-8">
      <EmptyState
        title="No open confirmations"
        description="Every open inbound PO either has a vendor-confirmed ship date or hasn't been picked up by the reconciler yet — it runs every 10 minutes."
      />
    </div>
  )
}
